<template>
    <div :id="svgContainerId" v-on="treeContainerHandlers">
        <svg :id="svgId"></svg>
        <NodeContextMenu ref="NodeContextMenu" @action="doContextAction"></NodeContextMenu>
    </div>
</template>
<script>
import './Graph.scss'
import { CardType, GraphType, NodeContextMenuAction, NodeType } from '@/utils/const'
import { AppEventBus, AppEventBusType } from '@/eventbus/app.eventbus'
import draw from '@/views/Scenario/draw/draw'
import { mapState } from 'vuex'
import NodeContextMenu from '@/views/Scenario/NodeContextMenu'
import FormRules from '@/modules/fptai/utils/FormRules'

const svgId = 'svgId'
const svgContainerId = 'svgContainerId'
let moveRoot = {
    x: 0,
    y: 0,
    moving: false,
    mouseEnter: false
}

export default {
    name: 'ScenarioGraph',
    components: {
        NodeContextMenu
    },
    data () {
        document.addEventListener('mouseup', this.stopMoving)
        let zoomOptions = []
        for (let i = 20; i <= 200; i += 10) {
            zoomOptions.push({
                text: i + '%',
                value: i
            })
        }
        return {
            svgId,
            svgContainerId,
            graphConfig: {
                zoom: 100,
                cellWidth: 200,
                cellHeight: 90,
                rectWidth: 200,
                rectHeight: 60,
                circleRadius: 14,
                toggleRadius: 7,
                reuseHeight: 17,
                reuseWidth: 38,
                svgWidth: window.innerWidth / 2,
                svgHeight: 10000,
                svgPadding: 20,
                duration: 500
            },
            zoomStep: 10,
            // graph
            graphData: null,
            nodeOpenMap: {},
            graphTypeData: null,
            graphType: GraphType.FLOW_STANDARD,
            zoomOptions: zoomOptions,
            svgViewBox: {
                width: 0,
                height: 0
            },
            treeContainerHandlers: {
                mouseenter: () => {
                    moveRoot.mouseEnter = true
                },
                mouseleave: () => {
                    moveRoot.mouseEnter = false
                },
                mousedown: e => {
                    if (e.which !== 1) {
                        return
                    }
                    let container = document.getElementById(svgId)
                    if (!container) {
                        return
                    }
                    let style = container.style
                    style.left = style.left || '0'
                    style.top = style.top || '0'
                    moveRoot.x = e.pageX
                    moveRoot.y = e.pageY
                    moveRoot.moving = true
                    container.style.transition = 'none'
                },
                mousemove: e => {
                    if (moveRoot.moving) {
                        let style = document.getElementById(svgId).style
                        style.left = (parseInt(style.left) + e.pageX - moveRoot.x) + 'px'
                        style.top = (parseInt(style.top) + e.pageY - moveRoot.y) + 'px'
                    }
                    moveRoot.x = e.pageX
                    moveRoot.y = e.pageY
                },
                mousewheel: e => {
                    if (!e.ctrlKey) {
                        return
                    }
                    e.preventDefault()
                    if (e.deltaY < 0) {
                        this.zoomIn()
                    } else {
                        this.zoomOut()
                    }
                }
            }
        }
    },
    computed: {
        ...mapState({
            shouldUpdateGraph: state => state.scenario.shouldUpdateGraph,
            activeGraphCardNodeId: state => state.scenario.activeGraphCardNodeId,
            activeGraphStepNodeId: state => state.scenario.activeGraphStepNodeId,
            activeScenarioCode: state => state.scenario.activeScenarioCode,
            activeStepCode: state => state.scenario.activeStepCode,
            activeScenario: state => state.scenario.activeScenario,
            activeStep: state => state.scenario.activeStep,
            activeCardCode: state => state.scenario.activeCardCode
        })
    },
    mounted () {
        AppEventBus.on(AppEventBusType.ZOOM_IN, this.zoomIn)
        AppEventBus.on(AppEventBusType.ZOOM_OUT, this.zoomOut)
        AppEventBus.on(AppEventBusType.ZOOM, this.setZoomValue)
        AppEventBus.on(AppEventBusType.SCROLL_TO_ACTIVE_NODE, this.scrollNodeToView)
        document.addEventListener('mouseup', this.stopMoving)
    },
    destroyed () {
        AppEventBus.off(AppEventBusType.ZOOM_IN, this.zoomIn)
        AppEventBus.off(AppEventBusType.ZOOM_OUT, this.zoomOut)
        AppEventBus.off(AppEventBusType.ZOOM, this.setZoomValue)
        AppEventBus.off(AppEventBusType.SCROLL_TO_ACTIVE_NODE, this.scrollNodeToView)
        document.removeEventListener('mouseup', this.stopMoving)
    },
    methods: {
        setZoomValue (zoom) {
            if (zoom !== this.graphConfig.zoom) {
                this.graphConfig.zoom = zoom
                this.zoom(zoom)
            }
        },
        createGraphData (stepList) {
            stepList = stepList.sort((a, b) => a.created_time.localeCompare(b.created_time))
            let allStepToThisStepMap = {}
            let countToThisStepMap = {}
            let mapCodeToNode = {}
            let mapNameToNode = {}
            let nodeList = []
            for (let step of stepList) {
                let triggerList = step.config.filter(config => config.type === 'trigger')
                let node = {
                    id: step.code,
                    name: step.name,
                    type: NodeType.STEP,
                    data: step,
                    children: [],
                    isRoot: triggerList.length > 0,
                    intentList: triggerList.map(trigger => trigger.config),
                    hasCondition: false,
                    parent: null
                }
                nodeList.push(node)
                mapCodeToNode[step.code] = node
                mapNameToNode[step.name] = node
                allStepToThisStepMap[step.code] = []
                countToThisStepMap[step.code] = 0
            }
            for (let step of stepList) {
                for (let card of step.cards) {
                    let nodeSource = mapCodeToNode[card.step_code]
                    let nodeCard = {
                        id: card.code,
                        name: card.name,
                        type: NodeType.CARD,
                        data: card,
                        children: [],
                        isRoot: false,
                        hasCondition: false,
                        parent: nodeSource
                    }
                    if (card.config.conditions && card.config.conditions.length > 0) {
                        nodeCard.hasCondition = true
                    }
                    if (card.card_type_id === CardType.GO_TO_STEP) {
                        let stepCodeList = card.config.step_codes.filter(code => !!code)
                        if (stepCodeList.length > 0) {
                            nodeSource.children.push(nodeCard)
                            nodeList.push(nodeCard)
                            for (let nodeTargetCode of stepCodeList) {
                                let nodeTarget = mapCodeToNode[nodeTargetCode]
                                nodeCard.children.push(nodeTarget)
                                nodeTarget.parent = nodeTarget.parent || nodeCard
                                countToThisStepMap[nodeTarget.id]++
                                allStepToThisStepMap[nodeTarget.id].push(nodeSource)
                            }
                        }
                    } else if (card.card_type_id === CardType.CAROUSEL) {
                        for (let carousel of card.config.carousel_cards) {
                            this.processButtonList(
                                carousel.buttons,
                                nodeCard,
                                nodeSource,
                                mapCodeToNode,
                                mapNameToNode,
                                countToThisStepMap,
                                allStepToThisStepMap
                            )
                        }
                        if (nodeCard.children.length > 0) {
                            nodeSource.children.push(nodeCard)
                            nodeList.push(nodeCard)
                        }
                    } else if (card.config.buttons && card.config.buttons.length > 0) {
                        this.processButtonList(
                            card.config.buttons,
                            nodeCard,
                            nodeSource,
                            mapCodeToNode,
                            mapNameToNode,
                            countToThisStepMap,
                            allStepToThisStepMap
                        )
                        if (nodeCard.children.length > 0) {
                            nodeSource.children.push(nodeCard)
                            nodeList.push(nodeCard)
                        }
                    }
                }
            }

            this.graphConfig.svgWidth = window.innerWidth - 760
            return {
                nodeList,
                mapCodeToNode,
                mapNameToNode,
                allStepToThisStepMap,
                countToThisStepMap
            }
        },
        processButtonList (buttonList, nodeCard, nodeSource, mapCodeToNode, mapNameToNode, countToThisStepMap, allStepToThisStepMap) {
            let nodeTargetList = []
            for (let button of buttonList) {
                if (button.payload) {
                    let nodeTargetName = button.payload.split('#')[0]
                    let nodeTarget = mapNameToNode[nodeTargetName]
                    nodeTargetList.push(nodeTarget)
                } else if (button.webview) {
                    let webviewCode = button.webview
                    let webview = this.$store.state.scenario.webViewList.find(webview => webview.code === webviewCode)
                    if (webview) {
                        let config = webview.config
                        if (config.type === 'custom' && config.call_back_step) {
                            let nodeTarget = mapCodeToNode[config.call_back_step]
                            nodeTargetList.push(nodeTarget)
                        }
                    }
                }
            }
            for (let nodeTarget of nodeTargetList) {
                if (nodeTarget && !nodeCard.children.includes(nodeTarget)) {
                    nodeCard.children.push(nodeTarget)
                    nodeTarget.parent = nodeTarget.parent || nodeCard
                    countToThisStepMap[nodeTarget.id]++
                    allStepToThisStepMap[nodeTarget.id].push(nodeSource)
                }
            }
        },
        drawGraph () {
            if (!this.graphData) {
                return
            }
            draw({
                svgId: this.svgId,
                data: this.graphData,
                nodeOpenMap: this.nodeOpenMap,
                openNode: null,
                closeNode: null,
                activeGraphCardNodeId: this.activeGraphCardNodeId,
                activeGraphStepNodeId: this.activeGraphStepNodeId,
                activeScenarioCode: this.activeScenarioCode,
                activeStepCode: this.activeStepCode,
                activeCardCode: this.activeCardCode,
                graphConfig: this.graphConfig,
                graphTypeData: this.graphTypeData,
                graphType: this.graphType,
                onNodeClick: this.onNodeClick,
                onContextMenu: this.onContextMenu,
                syncActiveGraphNode: this.syncActiveGraphNode,
                syncViewBox: this.syncViewBox
            })
        },
        zoomIn () {
            if (this.graphConfig.zoom < this.zoomOptions[this.zoomOptions.length - 1].value) {
                this.graphConfig.zoom += this.zoomStep
                this.zoom(this.graphConfig.zoom)
            }
        },
        zoomOut () {
            if (this.graphConfig.zoom > this.zoomOptions[0].value) {
                this.graphConfig.zoom -= this.zoomStep
                this.zoom(this.graphConfig.zoom)
            }
        },
        zoom (zoom) {
            let svg = document.getElementById(svgId)
            svg.style.transition = 'all 0.3s ease'
            if (moveRoot.mouseEnter) {
                let svgRect = svg.getBoundingClientRect()
                let containerRect = document.getElementById(svgContainerId).getBoundingClientRect()
                let rateXLeft = (svgRect.x - moveRoot.x) / svgRect.width
                let rateYTop = (svgRect.y - moveRoot.y) / svgRect.height
                let left = moveRoot.x + this.svgViewBox.width * rateXLeft * zoom / 100 - containerRect.x
                let top = moveRoot.y + this.svgViewBox.height * rateYTop * zoom / 100 - containerRect.y
                svg.style.left = left + 'px'
                svg.style.top = top + 'px'
            }
            AppEventBus.emit(AppEventBusType.ZOOM, zoom)
        },
        stopMoving () {
            if (moveRoot.moving) {
                moveRoot.moving = false
                let svg = document.getElementById(svgId)
                if (svg) svg.style.transition = 'all 0.3s ease'
                this.alignSvg()
            }
        },
        alignSvg () {
            let svg = document.getElementById(svgId)
            let style = svg.style
            let top = parseInt(style.top)
            let left = parseInt(style.left)
            let svgSize = {
                width: Number(svg.getAttribute('width')),
                height: Number(svg.getAttribute('height'))
            }
            let containerRect = document.getElementById(svgContainerId).getBoundingClientRect()
            if (top > 0) {
                top = 0
            } else {
                if (svgSize.height <= containerRect.height) {
                    top = 0
                } else if (svgSize.height + top < containerRect.height) {
                    top = containerRect.height - svgSize.height
                }
            }
            if (left > 0) {
                left = 0
            } else {
                if (svgSize.width <= containerRect.width) {
                    left = 0
                } else if (svgSize.width + left < containerRect.width) {
                    left = containerRect.width - svgSize.width
                }
            }
            style.left = left + 'px'
            style.top = top + 'px'
        },
        scrollNodeToView () {
            let svg = document.getElementById(this.svgId)
            let activeGraphNodeId = this.activeGraphCardNodeId || this.activeGraphStepNodeId
            if (!svg || !activeGraphNodeId) {
                return
            }
            let element = document.getElementById(`gCell-${activeGraphNodeId}`)
            if (!element) {
                return
            }
            new Promise((resolve) => {
                setTimeout(
                    () => {
                        resolve(null)
                    },
                    element.classList.contains('transitioning') ? this.graphConfig.duration + 10 : 0
                )
            }).then(() => {
                let container = document.getElementById(this.svgContainerId)
                let elementRect = element.getBoundingClientRect()
                let svgRect = svg.getBoundingClientRect()
                let containerRect = container.getBoundingClientRect()
                let isViewX = containerRect.x < elementRect.x && elementRect.x < containerRect.x + containerRect.width
                let isViewY = containerRect.y < elementRect.y && elementRect.y < containerRect.y + containerRect.height
                if (!isViewX) {
                    let x = containerRect.width / 2 - elementRect.x + svgRect.x
                    svg.style.left = x + 'px'
                }
                if (!isViewY) {
                    let y = containerRect.height / 2 - elementRect.y + svgRect.y
                    svg.style.top = y + 'px'
                }
                this.alignSvg()
            })
        },
        onNodeClick (node) {
            if (node.data.type === 'CARD') {
                this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                    activeGraphCardNodeId: node.id
                })
            } else {
                this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                    activeGraphStepNodeId: node.id
                })
            }
            this.drawGraph()
        },
        onContextMenu (e, node) {
            this.$refs.NodeContextMenu.open(e, node, this.graphType)
        },
        doContextAction (action) {
            let {
                name,
                item
            } = action
            switch (name) {
                case NodeContextMenuAction.RENAME:
                    this.$appService.useEventBus(AppEventBusType.EDIT_TEXT, {
                        text: item.name,
                        rules: [FormRules.ruleRequired, FormRules.ruleNoHtmlTag]
                    }).then(name => {
                        let step = item.data.data
                        this.$store.dispatch(this.$at.scenario.UPDATE_STEP, {
                            code: step.code,
                            name: name,
                            active: step.activate,
                            position: step.position,
                            scenario_code: step.scenario_code
                        })
                    })
                    break
                case NodeContextMenuAction.DELETE:
                    this.$appService.confirm(
                        this.$f.tc('confirmDelete', { item: item.name })
                    ).then(confirm => {
                        if (confirm) {
                            this.$store.dispatch(
                                this.$at.scenario.DELETE_STEP,
                                item.data.data
                            ).then(() => {
                                this.$forceUpdate()
                            })
                        }
                    })
                    break
                case NodeContextMenuAction.FLOW_STANDARD:
                    this.graphType = GraphType.FLOW_STANDARD
                    this.drawGraph()
                    this.resetSvg()
                    break
                case NodeContextMenuAction.FLOW_TO_NODE:
                    this.graphType = GraphType.FLOW_TO_NODE
                    this.graphTypeData = item.data.id
                    this.drawGraph()
                    this.resetSvg()
                    break
                case NodeContextMenuAction.FLOW_FROM_NODE:
                    this.graphType = GraphType.FLOW_FROM_NODE
                    this.graphTypeData = item.data.id
                    this.drawGraph()
                    this.resetSvg()
                    break
                case NodeContextMenuAction.FLOW_CONTAIN_NODE:
                    this.graphType = GraphType.FLOW_CONTAIN_NODE
                    this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, { activeStepCode: item.data.id })
                    this.graphTypeData = item.data.id
                    this.resetSvg()
                    break
            }
            this.$refs.NodeContextMenu.close()
        },
        syncActiveGraphNode (data) {
            this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                activeStepCode: this.$store.state.scenario.activeStepCode,
                activeCardCode: this.$store.state.scenario.activeCardCode,
                ...data
            })
        },
        syncViewBox (viewBox) {
            this.svgViewBox = viewBox
        },
        resetSvg () {
            let svg = document.getElementById(this.svgId)
            svg.style.left = '0'
            svg.style.top = '0'
        }
    },
    watch: {
        graphConfig: {
            deep: true,
            handler: function () {
                this.drawGraph()
            }
        },
        shouldUpdateGraph () {
            this.graphData = this.createGraphData(this.$store.state.scenario.stepList)
            this.drawGraph()
        },
        activeGraphCardNodeId () {
            this.scrollNodeToView()
        },
        activeGraphStepNodeId () {
            this.scrollNodeToView()
        },
        activeScenarioCode () {
            this.doContextAction({
                name: NodeContextMenuAction.FLOW_STANDARD
            })
            let svg = document.getElementById(this.svgId)
            svg.style.left = '0'
            svg.style.top = '0'
            setTimeout(this.scrollNodeToView, this.graphConfig.duration + 10)
        },
        '$i18n.locale': 'drawGraph'
    }
}
</script>
